import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, UserPlus } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Loader, Pill } from "../components/common";

const STATUSES = ["active", "trialing", "free", "past_due", "canceled"];
const blank = { name: "", email: "", password: "", coach_id: "", package_id: "", subscription_status: "active", phone: "", primary_goal: "" };

export default function Customers() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = (user.roles || [user.role]).includes("admin");
  const [customers, setCustomers] = useState(null);
  const [q, setQ] = useState("");
  const [packages, setPackages] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/coach/customers").then(({ data }) => setCustomers(data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/packages").then(({ data }) => setPackages(data)).catch(() => {});
    if (isAdmin) api.get("/admin/coaches").then(({ data }) => setCoaches(data)).catch(() => {});
  }, []);
  if (!customers) return <Loader />;

  const filtered = customers.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()) || c.email.toLowerCase().includes(q.toLowerCase()));

  const save = async () => {
    if (!form.name || !form.email || !form.password) return toast.error("Name, email and password are required.");
    setBusy(true);
    try {
      const payload = { ...form, package_id: form.package_id || null, coach_id: form.coach_id || null };
      await api.post("/admin/customers", payload);
      toast.success("Customer added!");
      setForm(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add customer."); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-heading text-5xl">Customers</h1>
        <button data-testid="customer-new" onClick={() => setForm({ ...blank })} className="px-5 py-2.5 bg-brand text-white rounded-lg font-bold uppercase text-sm tracking-wide flex items-center gap-2 hover:bg-brand-hover"><UserPlus className="w-4 h-4" /> Add Customer</button>
      </div>
      <div className="relative mt-5 max-w-md">
        <Search className="w-4 h-4 text-tertiary absolute left-3 top-1/2 -translate-y-1/2" />
        <input data-testid="customer-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customers..."
          className="w-full bg-surface-elevated border border-border rounded-lg pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-brand" />
      </div>

      <div className="mt-6 card-surface rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="text-left label-xs border-b border-border">
            <th className="px-4 py-3">Customer</th><th className="px-4 py-3 hidden md:table-cell">Plan</th>
            <th className="px-4 py-3">Completion</th><th className="px-4 py-3 hidden md:table-cell">Tags</th><th className="px-4 py-3">Status</th>
          </tr></thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan="5" className="px-4 py-10 text-center text-tertiary">No customers yet. Add one or wait for signups.</td></tr>}
            {filtered.map((c) => (
              <tr key={c.id} data-testid={`customer-row-${c.id}`} onClick={() => navigate(`/coach/customers/${c.id}`)} className="border-b border-white/5 hover:bg-white/5 cursor-pointer">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-brand/20 flex items-center justify-center font-heading text-brand">{c.name[0]}</div>
                    <div><p className="font-semibold">{c.name}</p><p className="text-xs text-tertiary">{c.email}</p></div>
                  </div>
                </td>
                <td className="px-4 py-3 hidden md:table-cell text-secondary">{c.package_name}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2"><div className="w-16 h-1.5 bg-white/10 rounded-full"><div className="h-full bg-brand rounded-full" style={{ width: `${c.completion_rate}%` }} /></div><span className="font-num text-xs">{c.completion_rate}%</span></div>
                </td>
                <td className="px-4 py-3 hidden md:table-cell"><div className="flex gap-1 flex-wrap">{(c.tags || []).slice(0, 2).map((t) => <Pill key={t}>{t}</Pill>)}</div></td>
                <td className="px-4 py-3"><Pill tone={c.subscription_status === "active" ? "success" : c.subscription_status === "past_due" ? "danger" : "warning"}>{c.subscription_status}</Pill></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {form && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-5 z-50" onClick={() => !busy && setForm(null)}>
          <div className="card-surface rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-heading text-3xl">Add Customer</h2>
            <p className="text-secondary text-sm mt-1">Create a free, trial, or comped account with login set by you.</p>
            <div className="space-y-3 mt-4">
              <input data-testid="cust-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" className="w-full bg-surface-elevated border border-border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-brand" />
              <input data-testid="cust-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="w-full bg-surface-elevated border border-border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-brand" />
              <input data-testid="cust-password" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Initial password" className="w-full bg-surface-elevated border border-border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-brand" />
              <div className="grid grid-cols-2 gap-2">
                <div><label className="label-xs block mb-1">Plan</label>
                  <select data-testid="cust-plan" value={form.package_id} onChange={(e) => setForm({ ...form, package_id: e.target.value })} className="w-full bg-surface-elevated border border-border rounded-lg px-2 py-2.5 text-sm">
                    <option value="">No plan</option>
                    {packages.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div><label className="label-xs block mb-1">Status</label>
                  <select data-testid="cust-status" value={form.subscription_status} onChange={(e) => setForm({ ...form, subscription_status: e.target.value })} className="w-full bg-surface-elevated border border-border rounded-lg px-2 py-2.5 text-sm capitalize">
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              {isAdmin && (
                <div><label className="label-xs block mb-1">Assign coach</label>
                  <select data-testid="cust-coach" value={form.coach_id} onChange={(e) => setForm({ ...form, coach_id: e.target.value })} className="w-full bg-surface-elevated border border-border rounded-lg px-2 py-2.5 text-sm">
                    <option value="">Auto / unassigned</option>
                    {coaches.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              )}
              <input data-testid="cust-goal" value={form.primary_goal} onChange={(e) => setForm({ ...form, primary_goal: e.target.value })} placeholder="Primary goal (optional)" className="w-full bg-surface-elevated border border-border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-brand" />
              <div className="flex gap-2 pt-1">
                <button data-testid="cust-save" onClick={save} disabled={busy} className="flex-1 py-3 bg-brand text-white font-bold uppercase tracking-wide rounded-lg hover:bg-brand-hover disabled:opacity-60">{busy ? "Adding…" : "Add Customer"}</button>
                <button onClick={() => setForm(null)} className="px-4 border border-white/20 rounded-lg">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
